import type { AlertsResult } from '../lib/types';
import { mdaName } from '../lib/types';
import { useApiQuery } from '../lib/useApi';
import { EmptyState, ErrorNote, PageHeader, Spinner } from '../ui/components';

/** GET /alerts is a real computed query (not a stored alert list) — see
 * packages/modules/c14-analytics/src/service/alerts.ts. It compares today's
 * SLA-breach count per MDA (Mahe calendar day) against that MDA's trailing
 * 7-day daily average, firing only when today is more than 2x the average
 * AND at least 3 breaches (the floor exists so a single breach against a
 * zero baseline doesn't read as an "infinite" spike). */
export function Alerts() {
  const alertsQ = useApiQuery<AlertsResult>('/api/v1/analytics/alerts');
  const data = alertsQ.data;

  return (
    <div>
      <PageHeader
        title="SLA breach alerts"
        subtitle={
          data
            ? `Flags an MDA whose SLA breaches today are more than ${data.thresholdMultiplier}× its trailing ${data.windowDays}-day daily average (minimum ${data.minAlertCount} breaches to fire)`
            : 'Loading alert thresholds…'
        }
      />

      {alertsQ.error ? (
        <div className="mb-4">
          <ErrorNote message={alertsQ.error} />
        </div>
      ) : null}

      {alertsQ.loading ? (
        <Spinner />
      ) : (data?.alerts.length ?? 0) === 0 ? (
        <EmptyState
          title="No SLA breach spikes detected"
          hint={data ? `Evaluated ${data.evaluated} MDA(s) as of ${new Date(data.generatedAt).toLocaleString('en-GB')}` : undefined}
        />
      ) : (
        <div className="space-y-3">
          {data!.alerts.map((a) => (
            <div key={a.mdaCode} className="card flex items-center justify-between px-4 py-3.5">
              <div>
                <div className="font-semibold text-slate-900">{mdaName(a.mdaCode)}</div>
                <div className="text-xs text-slate-500">{a.mdaCode}</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold tabular-nums text-rose-600">
                  {a.today} breach{a.today === 1 ? '' : 'es'} today
                </div>
                <div className="text-xs text-slate-500">
                  {a.ratio !== null
                    ? `${a.ratio}× the trailing ${a.trailingDailyAvg}/day average`
                    : 'no breaches in the trailing 7 days — first spike'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {data ? (
        <p className="mt-6 text-xs text-slate-400">
          Evaluated {data.evaluated} MDA(s) as of {new Date(data.generatedAt).toLocaleString('en-GB')}.
        </p>
      ) : null}
    </div>
  );
}
